import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, Box, Typography, IconButton, Button, Chip,
  TextField, CircularProgress, Snackbar, Alert, Tooltip,
  Dialog as MUIDialog, DialogTitle, DialogActions,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";

const API_URL = "/api";

function getHeaders() {
  const userId = localStorage.getItem("user_id");
  return userId
    ? { "X-User-Id": String(userId), "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatFecha(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Hace un momento";
  if (mins < 60) return `Hace ${mins} min`;
  if (mins < 60 * 24) return `Hace ${Math.floor(mins / 60)} h`;
  return d.toLocaleString("es-MX", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const TRIGGER_LABEL = {
  apply_template: "Antes de aplicar plantilla",
  restore:        "Antes de restaurar otro",
  clear_intents:  "Antes de vaciar intents",
  other:          "Auto",
};

export default function SnapshotsDialog({ open, onClose, chatbotId, onRestored }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [confirmRestore, setConfirmRestore] = useState(null);  // snap a restaurar
  const [working, setWorking] = useState(false);
  const [snack, setSnack] = useState(null);

  const fetchSnapshots = useCallback(async () => {
    if (!chatbotId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/chatbots/${chatbotId}/snapshots`, {
        headers: getHeaders(),
      });
      if (r.ok) setSnapshots(await r.json());
    } catch (_) {} finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    if (open) fetchSnapshots();
  }, [open, fetchSnapshots]);

  const handleCrear = async () => {
    if (!nombre.trim()) return;
    setCreating(true);
    try {
      const r = await fetch(`${API_URL}/chatbots/${chatbotId}/snapshots`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ nombre: nombre.trim(), descripcion: descripcion.trim() || null }),
      });
      if (!r.ok) throw new Error();
      setNombre(""); setDescripcion("");
      setCreateDialog(false);
      setSnack({ severity: "success", msg: "Copia de seguridad creada" });
      fetchSnapshots();
    } catch (e) {
      setSnack({ severity: "error", msg: "Error creando snapshot" });
    } finally {
      setCreating(false);
    }
  };

  const handleRestore = async (snap) => {
    setWorking(true);
    try {
      const r = await fetch(
        `${API_URL}/chatbots/${chatbotId}/snapshots/${snap.id}/restore`,
        { method: "POST", headers: getHeaders() }
      );
      if (!r.ok) throw new Error();
      const data = await r.json();
      setConfirmRestore(null);
      setSnack({
        severity: "success",
        msg: `Restaurado: ${data.intents_restaurados} intents · ${data.kb_entries_restauradas} entradas KB`,
      });
      fetchSnapshots();
      onRestored?.();
    } catch (e) {
      setSnack({ severity: "error", msg: "Error restaurando" });
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = async (snap) => {
    if (!window.confirm(`¿Eliminar la copia "${snap.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      const r = await fetch(`${API_URL}/chatbots/${chatbotId}/snapshots/${snap.id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!r.ok) throw new Error();
      setSnack({ severity: "success", msg: "Copia eliminada" });
      fetchSnapshots();
    } catch (e) {
      setSnack({ severity: "error", msg: "Error eliminando" });
    }
  };

  const manualCount = snapshots.filter((s) => s.tipo === "manual").length;
  const autoCount = snapshots.filter((s) => s.tipo === "auto").length;

  return (
    <>
      <Dialog
        open={open} onClose={onClose}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: "85vh" } }}
      >
        {/* Header */}
        <Box sx={{
          px: 2.5, py: 1.75,
          display: "flex", alignItems: "center", gap: 1.5,
          borderBottom: "1px solid #e2e8f0",
        }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: 1.5,
            background: "linear-gradient(135deg, #6366f1, #4f46e5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff",
          }}>
            <HistoryRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a", lineHeight: 1.2 }}>
              Copias de seguridad
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "#94a3b8", fontWeight: 500 }}>
              {manualCount}/20 manuales · {autoCount}/10 automáticas
            </Typography>
          </Box>
          <Button
            size="small" variant="contained"
            startIcon={<AddRoundedIcon sx={{ fontSize: 16 }}/>}
            onClick={() => setCreateDialog(true)}
            sx={{
              textTransform: "none", fontWeight: 700, fontSize: 12,
              borderRadius: 2, px: 1.5, py: 0.5,
              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
            }}
          >
            Crear copia
          </Button>
          <IconButton size="small" onClick={onClose} sx={{ color: "#94a3b8" }}>
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2, background: "#fafbfc" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          ) : snapshots.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <HistoryRoundedIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }}/>
              <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: "#0f172a" }}>
                Sin copias de seguridad
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25, maxWidth: 360, mx: "auto", lineHeight: 1.5 }}>
                Crea una copia antes de hacer cambios importantes para poder volver atrás en 1 click.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
              {snapshots.map((s) => {
                const isAuto = s.tipo === "auto";
                return (
                  <Box key={s.id} sx={{
                    p: 1.5, borderRadius: 2,
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: isAuto ? "#f59e0b" : "#6366f1",
                      boxShadow: `0 2px 8px ${isAuto ? "rgba(245,158,11,0.15)" : "rgba(99,102,241,0.15)"}`,
                    },
                  }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: 1.5,
                        background: isAuto ? "#fef3c7" : "rgba(99,102,241,0.10)",
                        color: isAuto ? "#d97706" : "#6366f1",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {isAuto
                          ? <AutorenewRoundedIcon sx={{ fontSize: 15 }}/>
                          : <EditRoundedIcon sx={{ fontSize: 14 }}/>
                        }
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.15, flexWrap: "wrap" }}>
                          <Typography sx={{
                            fontWeight: 700, fontSize: 13, color: "#0f172a",
                            letterSpacing: -0.1,
                          }}>
                            {s.nombre}
                          </Typography>
                          {isAuto && (
                            <Chip
                              size="small" label="AUTO"
                              sx={{
                                height: 16, fontSize: 9, fontWeight: 800,
                                backgroundColor: "#fef3c7", color: "#92400e",
                                border: "none", letterSpacing: 0.4,
                              }}
                            />
                          )}
                        </Box>
                        {s.descripcion && (
                          <Typography sx={{
                            fontSize: 11.5, color: "#64748b", lineHeight: 1.4,
                            overflow: "hidden", textOverflow: "ellipsis",
                            display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
                          }}>
                            {s.descripcion}
                          </Typography>
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.4, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600 }}>
                            {formatFecha(s.created_at)}
                          </Typography>
                          <Typography sx={{ color: "#cbd5e1", fontSize: 10.5 }}>·</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <BoltRoundedIcon sx={{ fontSize: 11, color: "#6366f1" }}/>
                            <Typography sx={{ fontSize: 10.5, color: "#475569", fontWeight: 600 }}>
                              {s.intents_count}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <MenuBookRoundedIcon sx={{ fontSize: 11, color: "#0ea5e9" }}/>
                            <Typography sx={{ fontSize: 10.5, color: "#475569", fontWeight: 600 }}>
                              {s.kb_count}
                            </Typography>
                          </Box>
                          <Typography sx={{ color: "#cbd5e1", fontSize: 10.5 }}>·</Typography>
                          <Typography sx={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600 }}>
                            {formatSize(s.size_bytes)}
                          </Typography>
                          {isAuto && s.trigger && (
                            <>
                              <Typography sx={{ color: "#cbd5e1", fontSize: 10.5 }}>·</Typography>
                              <Typography sx={{ fontSize: 10.5, color: "#92400e", fontWeight: 600 }}>
                                {TRIGGER_LABEL[s.trigger] || s.trigger}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.25 }}>
                        <Tooltip title="Restaurar este estado">
                          <IconButton size="small"
                            onClick={() => setConfirmRestore(s)}
                            sx={{ color: "#6366f1", "&:hover": { backgroundColor: "rgba(99,102,241,0.08)" } }}>
                            <RestoreRoundedIcon sx={{ fontSize: 18 }}/>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small"
                            onClick={() => handleDelete(s)}
                            sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }}/>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog crear */}
      <MUIDialog
        open={createDialog} onClose={() => !creating && setCreateDialog(false)}
        fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff", px: 2.5, py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Nueva copia de seguridad</Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.85 }}>
            Captura el estado actual del chatbot
          </Typography>
        </Box>
        <DialogContent sx={{ p: 2.5 }}>
          <TextField
            label="Nombre" fullWidth size="small" autoFocus required
            value={nombre} onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Antes de cambiar el prompt"
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Descripción (opcional)" fullWidth size="small" multiline rows={2}
            value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Notas sobre por qué guardas esta copia"
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setCreateDialog(false)} disabled={creating}
            sx={{ textTransform: "none", color: "#64748b" }}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={!nombre.trim() || creating} onClick={handleCrear}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5,
              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
            }}
          >
            {creating ? <CircularProgress size={16} sx={{ color: "#fff" }}/> : "Crear copia"}
          </Button>
        </DialogActions>
      </MUIDialog>

      {/* Confirmar restore */}
      <MUIDialog
        open={!!confirmRestore} onClose={() => !working && setConfirmRestore(null)}
        fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1.25 }}>
          <RestoreRoundedIcon sx={{ color: "#6366f1" }}/>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Restaurar copia</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: "#475569", lineHeight: 1.6, mb: 1.5 }}>
            Vas a reemplazar el estado actual del chatbot con <strong>{confirmRestore?.nombre}</strong> ({confirmRestore?.intents_count} intents · {confirmRestore?.kb_count} entradas KB).
          </Typography>
          <Box sx={{
            p: 1.25, borderRadius: 2, background: "#eff6ff", border: "1px solid #bfdbfe",
          }}>
            <Typography sx={{ fontSize: 11.5, color: "#1e40af", lineHeight: 1.5 }}>
              💡 <strong>Antes de pisar el estado actual</strong>, vamos a crear una copia automática para que puedas volver si te arrepientes.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRestore(null)} disabled={working}
            sx={{ textTransform: "none", color: "#64748b" }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => handleRestore(confirmRestore)} disabled={working}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5,
              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
            }}
          >
            {working ? <CircularProgress size={16} sx={{ color: "#fff" }}/> : "Restaurar"}
          </Button>
        </DialogActions>
      </MUIDialog>

      <Snackbar
        open={!!snack} autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack && (
          <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}
